import { Component, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { GoogleMap, GoogleMapsModule, MapMarker } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { Subject } from 'rxjs';
import { defaultIfEmpty, take, takeUntil } from 'rxjs/operators';
import { normalizeModel, Tools } from 'src/app/classes/shared/tools';
import { Model } from 'src/app/interfaces/api/model';
import { Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { User } from 'src/app/interfaces/api/user';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';

@Component({
  selector: 'app-device-map',
  imports: [
    GoogleMapsModule,
    MapThemeDirective
  ],
  standalone: true,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss']
})

export class MapComponent implements OnChanges, OnDestroy {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @Input() trackers: any[] = [];
  @Input() currentTrackers: any[] = [];
  @Input() configs: any[] = [];
  units: any[] = [];
  title = 'google-maps';
  user: User | undefined;
  mapObject: any;
  projectXY = [0, 0];
  mapAngle: any;
  projectId: any;
  mapOptions: any;
  markerPosition: any;
  showMap = true;
  minUnitScale = 1;
  zoom = 15;
  colors: any = {
    worker: '#0066FF',
    crane: '#FFA500',
    equipment: '#FF3300',
    event: '#EEBB39',
    warning: '#FF6600',
    danger: '#DD4B39',
    primary: '#00A65A',
    success: '#3C8DBC',
    lemon: '#F3E612',
    teal: '#39CCCC',
    fuchsia: '#F012BE',
    dark: '#010101',
    info: '#666666',
    mute: '#808080',
    white: '#FFFFFF',
    black: '#000000',
    saumon: "#f88e55",
    default: '#99BBAA'
  };
  markers: any = {};
  altitude = 0;
  groundOverlay: any;
  loadedMarkers: any[] = [];
  icons: any;
  center?: google.maps.LatLng;
  bounds?: google.maps.LatLngBounds;
  destroy$ = new Subject<void>();

  constructor(
    private projectService: ProjectService,
    private trackService: TrackService,
    private modelService: ModelService,
    private tools: Tools) {

    this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
      this.getModel(project.model as string);
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: any): void {
    // Updating color of previously selected markers (white again)
    if (changes.currentTrackers && changes.currentTrackers.previousValue && changes.currentTrackers.previousValue.length)
      this.updateMarkers({ trackers: changes.currentTrackers.previousValue, selected: false });

    // Setting bounds and updating markers when there are newely selected markers
    if (changes.currentTrackers && changes.currentTrackers.currentValue && changes.currentTrackers.currentValue.length) {
      if (changes.currentTrackers.currentValue[0].lastTrack == undefined) return;

      let trackers = changes.currentTrackers.currentValue.filter((t: Tracker) => t.lastTrack && t.lastTrack.lat && t.lastTrack.lat != 0);

      if (trackers.length == 0) return;

      this.setBounds(trackers);
      this.updateMarkers({ trackers, selected: true });
      this.panToBounds();

      return;
    }

    // Cleaning markers when there are changes to trackers input
    if (changes.trackers && changes.trackers.currentValue && changes.trackers.currentValue.length) {
      this.cleanMarkers();
      this.trackers = changes.trackers.currentValue;
    }

    // When all markers got a lastTrack object (not a string), we don't make call to API
    if (this.trackers.length && this.trackers.length == this.trackers.filter(t => t.lastTrack && typeof t.lastTrack !== "string").length) {
      this.initTrackers();
      this.setBounds();
      this.panToBounds(6);

      return;
    }

    // Making call to Api to fetch lastTrack of each tracker
    let idsByDb = this.getTrackIdsByDb(this.trackers);
    if (!Object.keys(idsByDb).length) return;
    this.trackService.getTracksByDb(idsByDb).pipe(takeUntil(this.destroy$)).subscribe((tracks: Track[]) => {
      for (let tracker of this.trackers) {
        let database = tracker.project?.database;
        let track = tracks.find(t => t.id == tracker.lastTrack || tracker.lastTracks?.includes(t.id) || (t.trackerName == tracker.name && idsByDb[database] == t.id));
        if (track)
          tracker.lastTrack = track;
      }

      this.initTrackers();
      this.setBounds();
      this.panToBounds(6);
    });
  }

  private updateMarkers(data: { trackers: any[], selected: boolean }): void {
    const { trackers, selected } = data;
    for (let tracker of trackers) {
      let trackerId = (tracker.id || tracker._id).toString();
      if (this.markers[trackerId]) {
        this.markers[trackerId].marker.setLabel({
          text: tracker.name,
          color: selected ? this.colors.white : this.colors.black,
          className: `marker-label${selected ? " selected" : ""}`
        })
      }
    }
  }

  getTrackIdsByDb(trackers: any[]): any {
    if (!trackers || !trackers.length) return {};
    let idsByDb: any = {};
    for (let tracker of trackers) {
      if (!tracker.project?.database || (typeof tracker.lastTrack != "string" && !tracker.lastTracks?.length)) continue;
      let db = tracker.project.database;
      idsByDb[db] = idsByDb[db] ?? [];
      idsByDb[db].push(tracker.lastTrack || tracker.lastTracks[tracker.lastTracks.length - 1]);
    }
    return idsByDb;
  }

  setBounds(trackers?: any[]): void {
    trackers = trackers ?? this.trackers;

    if (trackers && trackers.length > 1) {
      let tracks = trackers.map(t => t.lastTrack);

      tracks = tracks.filter(t => !!t && this.isValidCoord(t.lat) && this.isValidCoord(t.lon));

      let north: number = Math.max(...tracks.map(t => t.lat));
      let south: number = Math.min(...tracks.map(t => t.lat));
      let west: number = Math.min(...tracks.map(t => t.lon));
      let east: number = Math.max(...tracks.map(t => t.lon));

      this.bounds = new google.maps.LatLngBounds(new google.maps.LatLng(south, west), new google.maps.LatLng(north, east));
    }

    else if (trackers && trackers.length == 1) {
      this.bounds = undefined;
      let track = trackers[0].lastTrack;

      if (track && track.lat && track.lon)
        this.center = new google.maps.LatLng(track.lat, track.lon);
      else
        this.center = undefined;
    }
    else {
      this.bounds = undefined;
      this.center = undefined;
    }
  }

  onMarkerClick(marker: MapMarker): void { // @ts-ignore
    this.center = new google.maps.LatLng(marker.latLng.lat(), marker.latLng.lng());
  }

  panToBounds(zoom?: number): void {
    if (!this.mapOptions) return;
    if (this.bounds && this.map?.googleMap) {
      this.map.googleMap.fitBounds(this.bounds);
      this.map.googleMap.panToBounds(this.bounds);
      if (zoom)
        this.map.googleMap.setZoom(zoom);
    }
    else
      this.panToCenter();
  }

  panToCenter(marker?: MapMarker): void {
    if (marker && this.map?.googleMap) { // @ts-ignore
      this.map.googleMap.panTo({ lat: marker.latLng.lat(), lng: marker.latLng.lng() });
      this.map.googleMap.setZoom(18);
    }
    else if (this.center && this.center.lat() && this.map?.googleMap) {
      this.map.googleMap.panTo(this.center);
      this.map.googleMap.setZoom(18);
    }
  }

  updateMap(object: Model) {
    if (this.groundOverlay) this.groundOverlay.setMap(null);
    if (object.mapImage && object.mapCoord)
      this.groundOverlay = new google.maps.GroundOverlay(object.mapImage, object.mapCoord)

    if (this.groundOverlay && this.map?.googleMap) {
      this.groundOverlay.setMap(this.map.googleMap);
    }
  }

  loadMap(object: Model) {
    let mapOptions: google.maps.MapOptions = {
      center: { lat: object.mapCenter?.lat || 0, lng: object.mapCenter?.lon || 0 },
      zoom: this.zoom ?? 15,
      tilt: 0,
      mapTypeId: google.maps.MapTypeId.SATELLITE
    }

    this.mapOptions = mapOptions;

    if (this.map?.googleMap) {
      this.updateMap(object);

      this.map.mapDblclick.pipe(takeUntil(this.destroy$)).subscribe((data: any) => {

        const streetView = this.map.getStreetView();
        streetView.setOptions({
          position: { lat: data.latLng.lat(), lng: data.latLng.lng() },
          pov: { heading: 70, pitch: -10 },
        });
        streetView.setVisible(true);
      });

      if (this.map.googleMap) {
        this.map.googleMap.setOptions({ zoomControl: true, gestureHandling: 'greedy' });

        setTimeout(() => {
          if (this.bounds || this.center)
            this.panToBounds(6);
        }, 300);
      }
    }
  }

  getModel(modelId: string) {

    this.modelService.getModel(modelId).pipe(takeUntil(this.destroy$)).pipe(take(1), defaultIfEmpty(null)).subscribe({
      next: async results => {
        try {
          if (!results) {
            console.warn('No model data received for modelId:', modelId);
            return;
          }

          const normalized = normalizeModel(results);
          let object = {
            mapCenter: normalized.mapCenter,
            mapCoord: normalized.mapCoord,
            origin: normalized.origin,
            mapImage: normalized.mapImage
          };

          this.mapObject = object;
          await this.loadMap(this.mapObject);
        } catch (e) {
          console.error(e)
        }
      }, error: (error) => {
        this.showMap = false;
        console.error(error)
      }
    });
  }

  async initTrackers() {
    try {
      await this.loadIcons();

      for (let i = 0; i < this.trackers.length; i++) {
        let tracker = this.trackers[i];
        let trackerId = (tracker.id || tracker._id).toString();
        let trackerName = tracker.name;
        let trackerIcon = "marker";
        let isDelayOk = this.tools.isWithinOfflineDelay(tracker, this.configs);
        let traceColor = isDelayOk ? 'primary' : 'danger';
        let size = 50 * this.minUnitScale;

        let icon = {
          url: ''
        }

        let svg, color;

        if (this.colors[traceColor]) color = this.colors[traceColor];
        else color = traceColor;
        if (!this.icons[trackerIcon]) {
          return;
        }

        svg = this.icons[trackerIcon].replaceAll('{{ color }}', color).replaceAll("{{ triangleColor }}", this.colors.dark)
        var regSize = new RegExp('{{ size }}', 'g');
        svg = svg.replace(regSize, size);
        var regScale = new RegExp('{{ scale }}', 'g');
        svg = svg.replace(regScale, this.minUnitScale);

        icon.url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

        let url: any = await this.loadImageAsPNG(icon.url, 40, 40)

        let textColor = traceColor || '#FFFFFF';

        let track = {
          lat: 0,
          lon: 0,
          x: 0,
          y: 0,
          z: 0
        }

        if (tracker.lastTrack) {
          track = tracker.lastTrack
        } else {
          continue;
        }

        if (!track || !this.isValidCoord(track.lat) || !this.isValidCoord(track.lon)) continue;

        if (this.markers[trackerId]) {

          if (this.markers[trackerId].collider && this.markers[trackerId].collider.mapPolygon) {
            this.markers[trackerId].collider.init();
          }
          if (this.markers[trackerId].marker) {
            this.markers[trackerId].marker.setIcon({ url })
            this.markers[trackerId].marker.setPosition(new google.maps.LatLng(track.lat, track.lon));
          }

          this.markers[trackerId].color = traceColor;
          this.markers[trackerId].lastTrack = track;
          this.markers[trackerId].marker.setLabel({
            text: tracker.name,
            color: textColor,
            className: `marker-label`
          });

        } else {
          let marker = new google.maps.Marker({
            title: tracker.name,
            label: {
              text: tracker.name,
              color: textColor,
              className: `marker-label`
            },
            position: { lat: track.lat, lng: track.lon },
            icon: url,
            map: this.map?.googleMap,
            clickable: true,
            opacity: 0.7
          });
          marker.addListener("click", (m: MapMarker) => {
            this.onMarkerClick(m);
            this.panToCenter(m);
          });

          let collider = tracker.collider ?? null;

          this.markers[trackerId] = {
            marker,
            name: trackerName,
            color: traceColor,
            collider,
            tracker,
          };

          const t = {
            id: trackerId,
            name: trackerName,
            show: true,
            showCollider: tracker.collider?.visible,
            color: traceColor,
          }

          this.loadedMarkers.push({ tracker: t, marker, collider })
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  isValidCoord(val: any) {
    return typeof val === 'number' && isFinite(val);
  }

  cleanMarkers(): void {
    this.loadedMarkers.forEach(m => m.marker.setMap(null));
    this.loadedMarkers = [];
    this.markers = {};
  }

  loadImageAsPNG(url: string, height: number, width: number) {
    return new Promise((resolve, reject) => {
      let sourceImage = new Image();

      sourceImage.onload = () => {
        let png = new Image();
        let cnv = document.createElement('canvas'); // doesn't actually create an element until it's appended to a parent,
        // so will be discarded once this function has done it's job
        cnv.height = height;
        cnv.width = width;

        let ctx: any = cnv.getContext('2d');

        ctx.drawImage(sourceImage, 0, 0, height, width);
        png.src = cnv.toDataURL(); // defaults to image/png

        resolve(png.src);
      }
      sourceImage.onerror = reject;

      sourceImage.src = url;
    });
  }

  loadIcons() {
    return new Promise((resolve, reject) => {

      try {
        var icons = ['marker'];

        var loadCount = 0;
        if (!this.icons) {
          this.icons = {};
          icons.forEach((icon) => {

            let xhr, url;
            url = `/img/map/${icon}.svg`;

            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.onload = async () => {
              this.icons[icon] = xhr.responseText;
              loadCount++;
              if (loadCount == icons.length) resolve(true)
            }
            xhr.send(null);
          })

        } else {
          resolve(true)
        }
      } catch (e) {
        console.error(e)
      }
    })
  }
}
